import React, { useEffect } from 'react';
import Head from 'next/head';

import { DEFAULT_DOMAIN, DEFAULT_DOMAIN_ORCHARD } from '~/app/lib/customDomain';
import { getIsProduction } from '~/app/lib/getSongwhipEnv';
import { useTracker } from '~/app/lib/tracker/useTracker';
import { OneTrustGroups } from '~/app/lib/oneTrust/types';
import { on } from '~/app/lib/utils/events';

const mapOneTrustGroup = (group: string) => {
  switch (group) {
    case OneTrustGroups.STRICTLY_NECESSARY:
      return 'strictly_necessary';
    case OneTrustGroups.PERFORMANCE:
      return 'performance';
    case OneTrustGroups.FUNCTIONAL:
      return 'functional';
    case OneTrustGroups.TARGETING:
      return 'targeting';
    case OneTrustGroups.SOCIAL_MEDIA:
      return 'social_media';
    default:
      return group;
  }
};

const scriptId = (() => {
  const hostWithoutSubdomain = location.host.replace(/^[^.]+\./, '');

  switch (hostWithoutSubdomain) {
    case DEFAULT_DOMAIN:
      return '01920a8a-14b1-73f2-b379-c8b85c951a70';
    case DEFAULT_DOMAIN_ORCHARD:
      return '01920a8a-a309-7b6e-95f5-23756bf86f8a';

    // we need to be aware if the OneTrust loaded on the different domain,
    // not for that it was set up, cookies will not be stored
    default:
      return '50db3430-ad8d-4545-bbba-6ed6d7a6feba';
  }
})();

const OneTrust = React.memo(
  ({ sendConsentEvent }: { sendConsentEvent?: boolean }) => {
    const { trackEvent } = useTracker();

    useEffect(() => {
      if (!sendConsentEvent) return;

      return on(
        window,
        'OneTrustGroupsUpdated',
        (event: CustomEvent<string[]>) => {
          trackEvent({
            type: 'gdpr-consent',
            groups: event.detail.map(mapOneTrustGroup),
          });
        }
      );
    }, [sendConsentEvent]);

    useEffect(() => {
      return () => {
        // Remove OneTrust banner and PC html when component is unmounted
        // to avoid UI persisting on pages where banner should not exist
        document.getElementById('onetrust-consent-sdk')?.remove();
      };
    }, []);

    return (
      <>
        <style jsx global>{`
          /* Re-write our global svg css style to avoid banner svg misplacement */
          #onetrust-consent-sdk svg,
          #ot-sdk-btn-floating svg {
            display: inline;
          }

          .ot-pc-footer-logo,
          #ot-sdk-btn-floating {
            display: none;
          }

          #onetrust-consent-sdk #onetrust-banner-sdk,
          #onetrust-consent-sdk #onetrust-pc-sdk * {
            outline: 0 !important;
          }

          #onetrust-pc-sdk .ot-pc-header,
          #onetrust-pc-sdk .ot-pc-footer,
          #onetrust-pc-sdk .ot-accordion-layout.ot-cat-item {
            border: 0 !important;
          }
        `}</style>
        <Head>
          <script
            src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
            data-document-language="true"
            type="text/javascript"
            charSet="UTF-8"
            data-testid="onetrust-script"
            data-domain-script={`${scriptId}${
              getIsProduction() ? '' : '-test'
            }`}
            async
          />
        </Head>
      </>
    );
  }
);

export default OneTrust;
